import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { FlightRecord, UserRole, NewsMessage } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../services/firebase';
import { ref, onValue, remove } from 'firebase/database';
import { TrashIcon, DocumentArrowDownIcon, EnvelopeIcon, PrinterIcon } from '../components/Icons';
import { useFlight } from '../contexts/FlightContext';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const ReportView = React.memo(forwardRef<HTMLDivElement, { reportData: FlightRecord }>(({ reportData }, ref) => {
    const { user } = useAuth();
    const { flightNo, etd } = reportData.counterDistribution;

    const renderTimestamps = (type: 'crew' | 'pilots') => {
        const crewMovementEntries =
            reportData.crewMovement && typeof reportData.crewMovement === 'object'
            ? Object.entries(reportData.crewMovement)
            : [];
            
        return (
            <div>
                <h4 className="text-xl font-semibold mb-2 capitalize text-blue-300">{type} Movement</h4>
                <ul className="space-y-1 text-sm">
                    {crewMovementEntries.map(([station, data]) => {
                        const timestamps = data?.[type];
                        return (
                            <li key={station} className="border-b border-slate-700 py-1">
                                <strong>{station}:</strong>
                                <div className="pl-4">
                                    {Array.isArray(timestamps) && timestamps.length > 0 ? timestamps.map((ts, i) => (
                                        <div key={i}>
                                            <span>{new Date(ts.time).toLocaleString('en-GB')} by {ts.by}</span>
                                            {ts.modified && <span className="text-amber-400 ml-2">(Modified)</span>}
                                        </div>
                                    )) : 'N/A'}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };
    
    const renderSection = (title: string, data: { [key: string]: string | undefined }) => (
        <div className="mt-4">
            <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-2 text-blue-400">{title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="text-sm"><strong>{key}:</strong> {value || 'N/A'}</div>
                ))}
            </div>
        </div>
    );

    const processNewsFeed = (newsFeedObj?: { [id: string]: Omit<NewsMessage, 'id'> }): NewsMessage[] => {
        if (!newsFeedObj) return [];
        return Object.entries(newsFeedObj)
            .map(([id, value]) => ({ id, ...value }))
            .sort((a, b) => a.timestamp - b.timestamp);
    };

    const reportNewsFeed = processNewsFeed(reportData.newsFeed);
    const scannedPassengers = reportData.scannedPassengers || [];

    const bookingSafe = reportData.counterDistribution.booking || { c: '', m: '', inf: '', totalPassengers: '0' };
    const finalFiguresSafe = reportData.counterDistribution.finalFigures || { c: '', m: '', inf: '', totalPassengers: '0', 'Total Bags': '', 'Crew Bags': '', 'Waved Bags': '', 'Wheel Chairs': '', 'Buggy': '', 'Final Fuel': '' };
    const atsrDataSafe = reportData.atsrData || { atsrText: '', excessBaggage: '', heavyBags: '', notes: '' };

    const bookingForDisplay = {
        'C': bookingSafe.c,
        'M': bookingSafe.m,
        'Inf': bookingSafe.inf,
        'Total Passengers': bookingSafe.totalPassengers,
    };

    const { c, m, inf, totalPassengers, 'Total Bags': totalBags, 'Crew Bags': crewBags, 'Waved Bags': wavedBags, 'Wheel Chairs': wheelChairs, 'Buggy': buggy, 'Final Fuel': finalFuel } = finalFiguresSafe;
    
    const finalFiguresForDisplay = {
        'C': c,
        'M': m,
        'Inf': inf,
        'Total Passengers': totalPassengers,
        'Total Bags': totalBags,
        'Crew Bags': crewBags,
        'Waved Bags': wavedBags,
        'Wheel Chairs': wheelChairs,
        'Buggy': buggy,
        'Final Fuel': finalFuel
    };

    const atsrDataForDisplay = {
        'ATSR Text': atsrDataSafe.atsrText,
        'Excess Baggage': atsrDataSafe.excessBaggage,
        'Heavy Bags': atsrDataSafe.heavyBags,
        'Notes': atsrDataSafe.notes,
    };

    return (
        <div ref={ref} className="bg-slate-800 text-slate-200 p-6 printable-content">
            <div className="text-center mb-6">
                <h1 className="text-4xl font-bold text-white">Flight Operations Report</h1>
                <h2 className="text-2xl font-semibold text-blue-300">{flightNo}</h2>
                <p className="text-slate-400">Date: {new Date(reportData.flightDate).toLocaleDateString('en-GB')} | ETD: {etd ? new Date(etd).toLocaleTimeString('en-GB') : 'N/A'}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderTimestamps('crew')}
                {renderTimestamps('pilots')}
            </div>

            {renderSection('Booking Figures', bookingForDisplay)}
            {renderSection('Counter Distribution', reportData.counterDistribution.counters)}
            {renderSection('Gate Assignments', reportData.counterDistribution.gates)}
            {renderSection('Final Figures', finalFiguresForDisplay)}
            {Object.values(atsrDataForDisplay).some(v => v) && renderSection('ATSR Data', atsrDataForDisplay)}
            
            {scannedPassengers.length > 0 && (
                <div className="mt-4">
                    <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-2 text-blue-400">Scanned Passengers</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                        {scannedPassengers.map(p => (
                            <li key={p.sequence}>{p.sequence}. {p.name} - Seat {p.seatNumber} ({p.note})</li>
                        ))}
                    </ul>
                </div>
            )}
            
            {reportNewsFeed.length > 0 && (
                <div className="mt-4">
                    <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-2 text-blue-400">News Feed</h3>
                    <ul className="space-y-2 text-sm">
                        {reportNewsFeed.map(msg => (
                            <li key={msg.id} className="border-b border-slate-700 py-1">
                                <span className="font-bold text-white">{msg.sender}:</span> {msg.text}
                                <span className="text-xs text-slate-500 ml-2">({new Date(msg.timestamp).toLocaleTimeString('en-GB')})</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            
            <div className="mt-8 text-center text-xs text-slate-500">
                Report generated by {user?.username} on {new Date().toLocaleString('en-GB')}
            </div>
        </div>
    );
}));

ReportView.displayName = 'ReportView';

const ReportsScreen: React.FC = () => {
    const [reports, setReports] = useState<FlightRecord[]>([]);
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
    const { user } = useAuth();
    const { showNotification } = useFlight();
    const reportViewRef = useRef<HTMLDivElement>(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        const reportsRef = ref(database, 'flightRecords');
        const unsubscribe = onValue(reportsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const reportList: FlightRecord[] = Object.values(data);
                reportList.sort((a, b) => {
                    const timeA = parseInt(a.id.split('-').pop() || '0', 10);
                    const timeB = parseInt(b.id.split('-').pop() || '0', 10);
                    return timeB - timeA;
                });
                setReports(reportList);
                if (!selectedReportId || !reportList.find(r => r.id === selectedReportId)) {
                    setSelectedReportId(reportList.length > 0 ? reportList[0].id : null);
                }
            } else {
                setReports([]);
                setSelectedReportId(null);
            }
        });
        return () => unsubscribe();
    }, [selectedReportId]);
    
    const selectedReport = reports.find(r => r.id === selectedReportId);

    const handleDeleteReport = async () => {
        if (!selectedReportId || !user || ![UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role)) {
            showNotification('You do not have permission to delete reports.', 'error');
            return;
        }
        if (window.confirm('Are you sure you want to permanently delete this report?')) {
            try {
                await remove(ref(database, `flightRecords/${selectedReportId}`));
                showNotification('Report deleted successfully.', 'success');
                setSelectedReportId(null);
            } catch (error) {
                showNotification('Failed to delete report.', 'error');
            }
        }
    };
    
    const handleExport = useCallback(async (type: 'pdf' | 'email' | 'print') => {
        if (!reportViewRef.current || !selectedReport) return;
        setIsExporting(true);
        try {
            const canvas = await html2canvas(reportViewRef.current, {
                scale: 2,
                backgroundColor: '#1e293b',
            });
            const imgData = canvas.toDataURL('image/png');
            
            if (type === 'pdf') {
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const imgProps= pdf.getImageProperties(imgData);
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                let heightLeft = pdfHeight;
                let position = 0;
                
                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
                heightLeft -= pdf.internal.pageSize.getHeight();

                while (heightLeft >= 0) {
                  position = heightLeft - pdfHeight;
                  pdf.addPage();
                  pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
                  heightLeft -= pdf.internal.pageSize.getHeight();
                }

                pdf.save(`report-${selectedReport.counterDistribution.flightNo}.pdf`);
            } else if (type === 'print') {
                const printWindow = window.open('', '_blank');
                printWindow?.document.write(`<html><head><title>Print Report</title></head><body style="margin:0;"><img src="${imgData}" style="width:100%;"></body></html>`);
                printWindow?.document.close();
                printWindow?.focus();
                setTimeout(() => {
                    printWindow?.print();
                    printWindow?.close();
                }, 500);
            } else if (type === 'email') {
                const subject = `Flight Report: ${selectedReport.counterDistribution.flightNo}`;
                const body = `Please find the attached flight report for ${selectedReport.counterDistribution.flightNo}.%0D%0A%0D%0A(You may need to copy and paste the report image into the email body).`;
                window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            }
        } catch (error) {
            console.error('Export failed:', error);
            showNotification('Failed to export report.', 'error');
        } finally {
            setIsExporting(false);
        }
    }, [selectedReport, showNotification]);

    return (
        <div className="flex flex-col md:flex-row h-full gap-4">
            <div data-tour-id="reports-dropdown" className="w-full md:w-1/3 lg:w-1/4 flex-shrink-0 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-3 flex flex-col">
                <h2 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-2">Available Reports</h2>
                <ul className="overflow-y-auto flex-grow">
                    {reports.map(report => (
                        <li key={report.id}>
                            <button
                                onClick={() => setSelectedReportId(report.id)}
                                className={`w-full text-left p-2 rounded-md mb-1 transition-colors ${selectedReportId === report.id ? 'bg-blue-600 text-white' : 'hover:bg-slate-700 text-slate-300'}`}
                            >
                                <span className="font-bold">{report.counterDistribution.flightNo}</span>
                                <span className="block text-xs text-slate-400">{new Date(report.flightDate).toLocaleDateString('en-GB')}</span>
                            </button>
                        </li>
                    ))}
                    {reports.length === 0 && <p className="text-center text-sm text-slate-500 mt-4">No reports found.</p>}
                </ul>
            </div>
            <div className="flex-grow overflow-y-auto">
                {selectedReport ? (
                    <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
                        <div className="p-4 border-b border-slate-700 flex flex-wrap gap-2 justify-between items-center sticky top-0 bg-slate-900/80 backdrop-blur-sm z-10">
                            <h2 className="text-xl font-bold text-white">Report for {selectedReport.counterDistribution.flightNo}</h2>
                            <div className="flex gap-2">
                                {user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && (
                                    <button onClick={handleDeleteReport} className="action-btn bg-red-600 hover:bg-red-700"><TrashIcon className="w-5 h-5 mr-2" />Delete</button>
                                )}
                                <button onClick={() => handleExport('pdf')} disabled={isExporting} className="action-btn bg-green-600 hover:bg-green-700"><DocumentArrowDownIcon className="w-5 h-5 mr-2" />PDF</button>
                                <button onClick={() => handleExport('email')} disabled={isExporting} className="action-btn bg-sky-600 hover:bg-sky-700"><EnvelopeIcon className="w-5 h-5 mr-2" />Email</button>
                                <button onClick={() => handleExport('print')} disabled={isExporting} className="action-btn bg-blue-600 hover:bg-blue-700"><PrinterIcon className="w-5 h-5 mr-2" />Print</button>
                            </div>
                        </div>
                        <div className="p-2">
                            <ReportView ref={reportViewRef} reportData={selectedReport} />
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-slate-500 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
                        <p>Select a report to view details.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportsScreen;
